import React, { useEffect, useState } from 'react';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';

interface MatchActivity {
  id: string;
  userId: string;
  provider: string;
  avgHeartRate: number | null;
  maxHeartRate: number | null;
  minHeartRate: number | null;
  calories: number | null;
  distanceMeters: number | null;
  hrZones: unknown;
  trainingLoad: number | null;
  matchedAutomatically: boolean;
  user: { id: string; name: string; avatar?: string | null };
}

interface Props {
  matchId: string;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

// Ranking de esfuerzo físico del partido, a partir de MatchActivity (Task 4).
// El endpoint ya devuelve las actividades ordenadas por calorías desc — no
// re-ordenar acá. Si el usuario no es participante del partido, la API
// responde 403, y cualquier error simplemente deja `activities` vacío: no es
// un estado de error visible, el jugador sin reloj conectado no ve la sección.
export function MatchEffortSection({ matchId }: Props) {
  const [activities, setActivities] = useState<MatchActivity[]>([]);

  useEffect(() => {
    let isMounted = true;
    fetch(getApiUrl(`/matches/${matchId}/activity`), { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : { activities: [] }))
      .then((data) => {
        if (isMounted) setActivities(data.activities || []);
      })
      .catch(() => {
        if (isMounted) setActivities([]);
      });
    return () => {
      isMounted = false;
    };
  }, [matchId]);

  if (activities.length === 0) return null;

  return (
    <div style={{ margin: `${theme.spacing.lg} 4px ${theme.spacing.md}` }}>
      <h3
        style={{
          fontSize: theme.fonts.sizeBody,
          fontWeight: theme.fonts.weightBold,
          color: theme.colors.text,
          margin: `0 0 ${theme.spacing.sm}`,
        }}
      >
        Esfuerzo físico
      </h3>
      <div
        style={{
          background: theme.colors.surface,
          borderRadius: theme.borderRadius.card,
          border: `1px solid ${theme.colors.border}`,
          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
        }}
      >
        {activities.map((activity, index) => (
          <div
            key={activity.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.sm,
              padding: `${theme.spacing.xs} 0`,
              borderTop: index > 0 ? `1px solid ${theme.colors.border}` : 'none',
            }}
          >
            <span
              style={{
                width: '20px',
                fontSize: theme.fonts.sizeSmall,
                fontWeight: theme.fonts.weightBold,
                color: theme.colors.textSecondary,
              }}
            >
              {index + 1}
            </span>
            {activity.user.avatar ? (
              <img
                src={activity.user.avatar}
                alt={activity.user.name}
                style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: theme.colors.primary,
                  color: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: theme.fonts.weightBold,
                }}
              >
                {activity.user.name[0]?.toUpperCase() || '?'}
              </div>
            )}
            <span
              style={{
                flex: 1,
                fontSize: theme.fonts.sizeSmall,
                color: theme.colors.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {activity.user.name}
            </span>
            <div style={{ display: 'flex', gap: theme.spacing.sm, fontSize: '12px', color: theme.colors.textSecondary }}>
              {activity.calories ? <span>{activity.calories} kcal</span> : null}
              {activity.avgHeartRate ? <span>{activity.avgHeartRate} ppm</span> : null}
              {activity.distanceMeters ? <span>{(activity.distanceMeters / 1000).toFixed(1)} km</span> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
